import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ShieldCheck,
  Cpu,
  Server,
  Network,
  Code2,
  Briefcase,
  Headphones,
  Users,
} from "lucide-react";
import { director, team } from "@/data/team";
import { Footer } from "@/components/Footer";

const ICONS = { ShieldCheck, Cpu, Server, Network, Code2, Briefcase, Headphones };

const tecnica = team.filter((m) =>
  ["alvaro-ferrandez", "david-molto", "sergio-beltran", "lucia-montesinos"].includes(m.id)
);
const admin = team.filter((m) => ["cristina-server", "paula-sanchis"].includes(m.id));

const Node = ({ m, lead }) => {
  const Icon = ICONS[m.icon] || Cpu;
  return (
    <div
      data-testid={`org-node-${m.id}`}
      className={`flex w-full items-center gap-3 rounded-xl border bg-white/[0.03] p-3 transition-colors duration-300 hover:border-cyber-cyan/40 ${
        lead ? "border-cyber-cyan/40 shadow-[0_0_28px_rgba(0,240,255,0.12)]" : "border-white/10"
      }`}
    >
      <div className="relative flex-none">
        <img
          src={m.photo}
          alt={m.name}
          loading="lazy"
          className={`rounded-full object-cover object-top grayscale contrast-[1.08] brightness-95 transition-all duration-500 hover:grayscale-0 ${lead ? "h-16 w-16 ring-2 ring-cyber-cyan/60" : "h-12 w-12 ring-1 ring-white/15"}`}
        />
        <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border border-cyber-cyan/40 bg-[#05070f] text-cyber-cyan">
          <Icon className="h-3 w-3" />
        </span>
      </div>
      <div className="min-w-0">
        <p className={`font-head font-bold text-white ${lead ? "text-lg" : "text-sm"}`}>{m.name}</p>
        <p className="truncate font-mono-tech text-[10px] uppercase tracking-widest text-cyber-cyan">{m.role}</p>
        <p className="truncate font-sans text-[11px] text-white/50">{m.area}</p>
      </div>
    </div>
  );
};

const Dept = ({ title, members }) => (
  <div className="flex flex-col items-center">
    <div className="h-8 w-px bg-white/15" />
    <div className="w-full rounded-2xl border border-white/10 bg-white/[0.015] p-5">
      <p className="mb-4 flex items-center justify-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-white/60">
        <Users className="h-3.5 w-3.5 text-cyber-violet" /> {title}
      </p>
      <div className="space-y-3">
        {members.map((m) => (
          <Node key={m.id} m={m} />
        ))}
      </div>
    </div>
  </div>
);

export default function OrgChart() {
  return (
    <div className="relative z-10 min-h-screen" data-testid="page-organigrama">
      <header className="border-b border-white/10 bg-[#05070f]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4 md:px-8">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/cyvr-logo.png" alt="CYVR" className="h-9 w-9 object-contain drop-shadow-[0_0_10px_rgba(0,240,255,0.5)]" />
            <span className="font-head text-sm font-bold tracking-tight text-white">CYVR<span className="text-cyber-cyan"> LABS</span></span>
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/equipo" className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-white/60 transition-colors hover:text-cyber-cyan" data-testid="org-to-team">
              <Users className="h-4 w-4" /> Equipo
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-white/60 transition-colors hover:text-cyber-cyan">
              <ArrowLeft className="h-4 w-4" /> Inicio
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-16 md:px-8 md:py-24">
        <p className="font-mono-tech text-[11px] uppercase tracking-[0.28em] text-cyber-cyan">Estructura</p>
        <h1 className="mt-4 font-head text-4xl font-bold tracking-tight text-white sm:text-5xl">Organigrama</h1>
        <p className="mt-5 max-w-2xl font-sans text-base leading-relaxed text-white/60">
          Estructura organizativa de CYVR Labs. La dirección coordina las áreas técnica y administrativa.
        </p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 flex flex-col items-center"
        >
          <div className="w-full max-w-md">
            <Node m={director} lead />
          </div>
          <div className="h-8 w-px bg-cyber-cyan/50" />
          <div className="h-px w-full max-w-3xl bg-white/15" />
          <div className="grid w-full max-w-3xl grid-cols-1 gap-6 md:grid-cols-2">
            <Dept title="Área Técnica" members={tecnica} />
            <Dept title="Administración" members={admin} />
          </div>
        </motion.div>

        <div className="mt-16 flex flex-wrap items-center gap-4">
          <Link
            to="/equipo"
            data-testid="org-view-team-btn"
            className="inline-flex items-center gap-2 rounded-full bg-cyber-cyan px-6 py-3 font-mono-tech text-xs font-bold uppercase tracking-widest text-[#05070f] transition-transform hover:scale-[1.03]"
          >
            <Users className="h-4 w-4" /> Ver fichas del equipo
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 font-mono-tech text-xs font-bold uppercase tracking-widest text-white/70 transition-colors hover:border-cyber-cyan hover:text-cyber-cyan"
          >
            Volver al inicio
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
